import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Avatar } from '@/components/ui/custom/dataDisplay/Avatar';
import PresenceCheckbox from '@/components/ui/custom/dataDisplay/PresenceCheckbox';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import Skeleton from '@/components/ui/custom/Skeleton';
import useStudentReport from '@/data/hook/useStudentReport';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import ScheduledLesson from '@/models/ScheduledLesson';
import {
  ActiveEnum,
  EnrollmentAndReportStatus,
  PresenceEnum,
  ScheduledLessonReportEnum,
  TranferredEnum,
} from '@/models/ScheduledLessonReport';
import { studentReportStatus } from '@/utils/studentReportStatus';
import { StudentReportAssessment as ReportAssessment } from '../teacher/StudentReportAssessment';
import NotifyLatelessButton from './NotifyLatelessButton';
import { StudentReportPermissions } from './StudentReports';
import VacationNoticeTag from '@/components/ui/custom/VacationNoticeTag';
import Klass from '@/models/Klass';
import moment from 'moment';
import useAuth from '@/data/hook/useAuth';
import { isSuperAdmin } from '@/functions/auth';
import { twJoin, twMerge } from 'tailwind-merge';
import { useEnrollmentStatusTranslation } from '@/data/hook/useEnrollmentStatusTranslation';
import { VacationNotice } from '@/models/VacationNotice';
import { StudentConectaTag } from '@/components/ui/custom/ConectaTag';
import { HomeworkActivityProgress } from '@/models/HomeworkActivity';
import StudentPresenceNotifier from './StudentPresenceNotifier';

type onConfirm = {
  presence: PresenceEnum;
  reportId: number;
};

type StudentReportProps = {
  enrollment: Enrollment;
  handleEnrollment: (enrollment: Enrollment) => void;
  permissions: StudentReportPermissions;
  scheduledLesson: ScheduledLesson;
  setButtonRef?: (e: HTMLElement) => void;
  klass: Klass;
  vacationNotice?: VacationNotice;
  homeworkProgress?: HomeworkActivityProgress;
};

export function StudentReport({
  enrollment,
  handleEnrollment,
  permissions,
  scheduledLesson,
  setButtonRef,
  klass,
  vacationNotice,
  homeworkProgress,
}: StudentReportProps) {
  const studentId = enrollment.student.id;
  const { user } = useAuth();

  const scheduledLessonId = scheduledLesson.id;

  const wasUpdatedAfterLesson = moment(scheduledLesson.datetime).isBefore(
    enrollment.updatedAt,
  );

  const {
    report,
    loadingReport,
    create,
    update,
    loadingCreate,
    loadingUpdate,
  } = useStudentReport({
    studentId,
    scheduledLessonId,
  });

  const [visible, setVisible] = useState(false);

  const name = handleUserFullName(enrollment.student);

  async function onConfirm({ presence, reportId: id }: onConfirm) {
    await update({ id, changes: { presence, presenceReward: null } });

    setVisible(false);
  }

  function onCancel() {
    setVisible(false);
  }

  async function handlePresence(presence: PresenceEnum) {
    if (report) {
      const id = report.id;
      if (presence === PresenceEnum.MISSED) return setVisible(true);
      else {
        const setedPresence = presence === PresenceEnum.NONE ? null : presence;
        await update({
          id,
          changes: { presence: setedPresence },
        });
      }
    } else {
      await create({
        student: studentId,
        scheduledLesson: scheduledLessonId,
        presence,
      });
    }
    handleEnrollment(enrollment);
  }

  async function handleActivation() {
    if (!report) return;

    const id = report.id;
    await update({
      id,
      changes: { status: ScheduledLessonReportEnum.ACTIVE },
    });
  }

  const status = studentReportStatus({
    enrollmentStatus: enrollment.status,
    reportStatus: report?.status,
    wasUpdatedAfterLesson,
  });

  const activeStatus: EnrollmentAndReportStatus[] = [
    EnrollmentStatusEnum.ACTIVE,
    ActiveEnum.CANCELED_IN_FUTURE,
    ActiveEnum.TRANSFERED_IN_FUTURE,
  ];

  function getReportPermission() {
    if (enrollment.status === EnrollmentStatusEnum.AWAITING_PAYMENT) {
      return false;
    }
    return activeStatus.includes(status) || isSuperAdmin(user?.userType);
  }

  const isReportEditAllowed = getReportPermission();

  const disabledClassname = isReportEditAllowed
    ? ''
    : 'disabled grayscale-[80]';

  const loading = loadingReport || loadingCreate || loadingUpdate;

  const [vacationRef, setVacationRef] = useState<HTMLElement | null>(null);

  return (
    <li
      data-testid="studentReport"
      className={`flex flex-col 2xl:flex-row flex-wrap gap-2 justify-start items-start ${
        loadingCreate ? 'disabled' : ''
      }`}
    >
      <RemoveAttendanceModal
        onCancel={onCancel}
        onConfirm={onConfirm}
        visible={visible}
        name={name}
        reportId={report?.id}
      />

      <div className="flex items-center gap-2 w-full flex-1">
        <PresenceCheckbox
          key={`presenceCheckbox${report?.id}`}
          presence={report?.presence}
          onClick={handlePresence}
          loading={loading}
          disabled={!permissions.hasPresencePermission || !isReportEditAllowed}
        />

        <Avatar
          testId="studentAvatar"
          size="6"
          userId={enrollment.student.id}
          className={disabledClassname}
        />

        <div className="flex w-full justify-between items-center relative gap-2">
          <ConditionalRenderer
            fallback={
              <Skeleton className="flex h-8 bg-primary-content rounded-md" />
            }
            condition={!loadingReport}
          >
            <span className="flex gap-2.5 flex-wrap">
              <MainButton
                text={name}
                disabled={
                  !permissions.hasReportsPermission || !isReportEditAllowed
                }
                onClick={() => handleEnrollment(enrollment)}
                color="custom"
                className={twJoin(
                  'flex text-primary hover:text-primary/40 text-ellipsis overflow-hidden font-rubik font-500 text-left',
                  disabledClassname,
                )}
              />

              <StudentConectaTag
                studentType={enrollment.student.studentType}
                className={disabledClassname}
              />
            </span>
            <StudentReportStatus
              className={disabledClassname}
              status={status}
            />
          </ConditionalRenderer>
          <div className="shrink-0">
            <NotifyLatelessButton
              key={scheduledLesson.id}
              scheduledLesson={scheduledLesson}
              scheduledLessonReport={report}
              enrollment={enrollment}
              klass={klass}
              setRef={setButtonRef}
            />
            <StudentPresenceNotifier
              enrollment={enrollment}
              klass={klass}
              scheduledLesson={scheduledLesson}
              report={report}
            />
          </div>
        </div>
      </div>

      <div className="border border-primary-content h-6 m-0 w-fit hidden 2xl:flex" />

      <ReportAssessment
        homeworkProgress={homeworkProgress}
        testId="reportAssessment"
        key={`reportAssessment${report?.id}`}
        report={report}
        loading={loadingReport}
        setRef={setVacationRef}
      />
      <VacationNoticeTag
        key={scheduledLesson.id}
        className={{
          base: 'z-50 py-0.5 px-1 text-14 lowercase',
        }}
        offset={[-70, -30]}
        target={vacationRef}
        render={!!vacationNotice}
      />

      <ActiveReportButton
        onActiveReport={handleActivation}
        status={status}
        locked={!permissions.hasPresencePermission}
        loading={loadingUpdate}
      />
    </li>
  );
}

type RemoveAttendanceModalProps = {
  name: string;
  reportId?: number;
  visible?: boolean;
  onConfirm({ presence, reportId }: onConfirm): Promise<void>;
  onCancel(): void;
};

function RemoveAttendanceModal({
  name,
  reportId,
  visible = false,
  onConfirm,
  onCancel,
}: RemoveAttendanceModalProps) {
  const presence = PresenceEnum.MISSED;

  if (reportId) {
    return (
      <ModalDisable
        modalType="confirm"
        selectedObjectName={name}
        onClickConfirm={async () => await onConfirm({ presence, reportId })}
        onClickCancel={onCancel}
        visible={visible}
        translationString="modalStudentAbsence"
      />
    );
  }

  return null;
}

type ReportStatusProps = {
  status: EnrollmentAndReportStatus;
  className?: string;
};

export function StudentReportStatus({ status, className }: ReportStatusProps) {
  const transalatedStatus = useEnrollmentStatusTranslation(status);

  return (
    <ConditionalRenderer condition={status !== EnrollmentStatusEnum.ACTIVE}>
      <Text
        text={transalatedStatus}
        className={twMerge(
          'absolute h-fit w-fit px-1.5 py-0.5 rounded-full left-3 -bottom-2 bg-primary-content text-12 whitespace-nowrap',
          className,
        )}
      />
    </ConditionalRenderer>
  );
}

type ActiveReportButtonProps = ReportStatusProps & {
  loading?: boolean;
  onActiveReport(): Promise<void>;
  locked: boolean;
};

function ActiveReportButton({
  status,
  locked,
  loading,
  onActiveReport,
}: ActiveReportButtonProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport',
  });

  return (
    <ConditionalRenderer
      condition={status === TranferredEnum.TRANSFERRED_TO_CLASS}
    >
      <ConditionalWrapper
        condition={locked}
        wrapper={<Tooltip text={t('isLockedDesc')} />}
      >
        <MainButton
          icon={<CheckCircleIcon />}
          text={t('activeStudent')}
          onClick={onActiveReport}
          disabled={locked}
          loading={loading}
        />
      </ConditionalWrapper>
    </ConditionalRenderer>
  );
}
